import React, { useCallback, useContext, useEffect, useState, type CSSProperties } from 'react';
import { app, pages } from '@microsoft/teams-js';
import {
	Input,
	Label,
	useId,
	Button,
	Dropdown,
	Option,
	DropdownProps,
	InputProps,
	MessageBar,
	Caption1,
} from '@fluentui/react-components';
import { TeamsFxContext } from '../context';
import { IPPPApp, IPPPProject, getAppAndWebUrls, getProjects, getTPGApps } from '../services';
import { useGraph } from '../hooks';
import { ClientTypeError, ConfigTable, IInstance } from '../interfaces';

const isValidUrl = (string: string) => {
	try {
		new URL(string);
		return true;
	} catch (err) {
		return false;
	}
};

const style: Record<string, CSSProperties> = {
	wrapper: {
		display: 'flex',
		flexDirection: 'column',
		gap: '2rem',
		// backgroundColor: 'white',
		height: '100%',
	},
	input: { display: 'flex', flexDirection: 'column', gap: '0.5rem' },
};

const tableNameSingularMap = new Map<ConfigTable, string>([
	['tpg_projects', 'tpg_project'],
	['msdyn_projects', 'msdyn_project'],
]);

/**
 * URLs for testing with @projectservice.ch
 *
 * My Tasks: https://org2e08b438.crm17.dynamics.com/main.aspx?appid=5467d40c-2c31-ee11-bdf4-002248cb0048&pagetype=entitylist&etn=tpg_actionitem
 * Projects: https://org2e08b438.crm17.dynamics.com/main.aspx?appid=5467d40c-2c31-ee11-bdf4-002248cb0048&pagetype=entitylist&etn=tpg_project
 * Reports:  https://org2e08b438.crm17.dynamics.com/main.aspx?appid=5467d40c-2c31-ee11-bdf4-002248cb0048&pagetype=dashboard&id=8ab93331-22df-eb11-bacb-000d3ab30a7c&type=system&_canOverride=true
 */

/**
 * The 'Config' component is used to display your group tabs
 * user configuration options.  Here you will allow the user to
 * make their choices and once they are done you will need to validate
 * their choices and communicate that to Teams to enable the save button.
 */
const TabConfig = () => {
	console.debug('[local] TabConfig render..');

	const { themeString } = useContext(TeamsFxContext);
	const tabNameId = useId();
	const urlId = useId();
	const beforeUrlId = useId();

	const [errors, setErrors] = React.useState('');
	const urlInput = React.useRef<HTMLInputElement>(null);
	const tabNameInput = React.useRef<HTMLInputElement>(null);

	// URL Input
	const [urlString, setUrlString] = useState<string>('');
	const onUrlChange: InputProps['onChange'] = (ev, data) => {
		setUrlString(data.value);
	};

	// Environment dropdown
	const envComboId = useId('env-combo');
	const [environmentOptions, setEnvironmentOptions] = useState<IInstance[]>([]);
	const [selectedEnvironment, setSelectedEnvironment] = React.useState<IInstance>();

	const onEnvOptionSelect: Partial<DropdownProps>['onOptionSelect'] = (ev, data) => {
		const env = graph.data?.environments?.find((e) => e.EnvironmentId === data.optionValue);
		if (env) {
			setSelectedEnvironment(env);
			updateUrl();
		}
	};

	// PPP App
	const [pppApp, setPppApp] = useState<IPPPApp>();
	const [table, setTable] = useState<ConfigTable>();

	// Project dropdown
	const projComboId = useId('proj-combo');
	const [projectPlaceholder, setProjectPlaceholder] = useState('');
	const [projectOptions, setProjectOptions] = useState<IPPPProject[]>([]);
	const [selectedProject, setSelectedProject] = React.useState<IPPPProject>();
	const [projectsLoaded, setProjectsLoaded] = useState<boolean>(false);

	const onProjOptionSelect: Partial<DropdownProps>['onOptionSelect'] = (ev, data) => {
		const project = projectOptions.find((p) => p.id === data.optionValue);
		if (project) {
			setSelectedProject(project);
			updateUrl();
		}
	};

	// Authenticate
	const graph = useGraph(() => console.debug('Config tab auth complete'));
	console.debug('useGraph', graph);

	useEffect(() => {
		if (graph.data?.environments?.length) {
			setEnvironmentOptions(graph.data?.environments);
		}
	}, [graph.data?.environments]);

	useEffect(() => {
		if (graph.error) {
			setErrors((graph.error as any)?.body ?? (graph.error as any)?.message ?? JSON.stringify(graph.error));
		}
		// If teamsContext.context updated trigger reload on Client type error
		else if (graph.data?.error instanceof ClientTypeError) {
			if (!graph.loading) {
				graph.reload();
			}
			return;
		}
		// Display any other errors
		else if (graph.data?.error) {
			setErrors(graph.data.error.message ?? graph.data.error.body ?? graph.data.error.code);
		}
	}, [graph, graph.data?.error, graph.error]);

	// Load projects and apps
	useEffect(() => {
		if (!graph.teamsUserCredential || !selectedEnvironment || !table) {
			return;
		}

		// Load the projects from the table that the user has access to
		const loadProjectList = async (table: ConfigTable) => {
			try {
				setProjectPlaceholder('Loading...');
				const projects = await getProjects(graph.teamsUserCredential!, selectedEnvironment, table);
				if (projects) {
					setProjectOptions(projects);
					setProjectsLoaded(true);

					setProjectPlaceholder('Select project');
				} else {
					setProjectPlaceholder('');
				}
			} catch (error: any) {
				setErrors(error?.message);
				setProjectPlaceholder('');
			}
		};

		// Load Project table
		loadProjectList(table);
	}, [graph.teamsUserCredential, selectedEnvironment, table]);

	// Validate the form - doesn't save config user must click
	const handleSubmit = () => {
		console.debug('[local] submitting..');
		const port = window.location.port === '' || window.location.port === '433' ? '' : `:${window.location.port}`;

		const baseUrl = `https://${window.location.hostname}${port}/index.html#/launch?url=`;
		let url = urlString; // urlInput.current?.value ?? '';

		if (url && !url.startsWith('https://')) {
			url = `https://${url}`;
		}

		if (!url) {
			return;
		}

		if (!isValidUrl(url)) {
			console.debug(url);
			setErrors('This is not a valid URL. Please provide a valid URL.');
			return;
		}

		// Initialize the Microsoft Teams SDK
		app.initialize().then(() => {
			/**
			 * When the user clicks "Save", save the url for your configured tab.
			 * This allows for the addition of query string parameters based on
			 * the settings selected by the user.
			 */
			pages.config.registerOnSaveHandler((saveEvent) => {
				const urls = getAppAndWebUrls(new URL(url), themeString === 'dark');

				if (!urls.appUrl || !urls.webUrl) {
					setErrors(`This is not a valid URL. Please provide a valid URL. ${urls.appUrl}`);
					return;
				}

				urls.appUrl = `${baseUrl}${encodeURIComponent(urls.appUrl)}`;

				console.debug('Tab URLs:', urls);

				let suggestedDisplayName = tabNameInput.current?.value ?? 'My Tab';

				pages.config
					.setConfig({
						suggestedDisplayName,
						entityId: btoa(url),
						contentUrl: urls.appUrl,
						websiteUrl: urls.webUrl,
					})
					.then(() => {
						saveEvent.notifySuccess();
					});
			});

			/**
			 * After verifying that the settings for your tab are correctly
			 * filled in by the user you need to set the state of the dialog
			 * to be valid.  This will enable the save button in the configuration
			 * dialog.
			 */
			pages.config.setValidityState(true);
		});
	};

	// Set the URL when env and project selected, then submit
	const updateUrl = useCallback(() => {
		if (!selectedEnvironment?.Url || !pppApp?.appmoduleid || !selectedProject?.id || !table) {
			return;
		}

		setUrlString(
			`${selectedEnvironment.Url}/main.aspx?appid=${
				pppApp.appmoduleid
			}&pagetype=entityrecord&etn=${tableNameSingularMap.get(table)}&id=${selectedProject.id}`
		);

		// handleSubmit();
	}, [pppApp?.appmoduleid, selectedEnvironment?.Url, selectedProject?.id, table]);

	// Load projects and apps
	useEffect(() => {
		if (!graph.teamsUserCredential || !selectedEnvironment) {
			return;
		}

		// Find the PPP app and set the correct project table
		const loadAppList = async () => {
			try {
				setProjectPlaceholder('Loading...');
				const apps = await getTPGApps(graph.teamsUserCredential!, selectedEnvironment);
				if (apps.length) {
					let app = apps.find((a) => a.uniquename === 'tpg_scheduler');
					if (app) {
						setTable('tpg_projects');
					} else {
						app = apps.find((a) => a.uniquename === 'tpg_TPGPowerProjectApp');
						setTable('msdyn_projects');
					}

					if (app) {
						setPppApp(app);
						updateUrl();
					} else {
						setProjectPlaceholder('TPG App not found!');
					}
				} else {
					setProjectPlaceholder('TPG App not found!');
				}
			} catch (error: any) {
				setErrors(error?.message);
				setProjectPlaceholder('');
			}
		};

		// Load app first
		loadAppList();
	}, [graph.teamsUserCredential, selectedEnvironment, updateUrl, urlString]);

	return (
		<div className={themeString === 'default' ? 'light' : themeString === 'dark' ? 'dark' : 'contrast'}>
			<div style={style.wrapper}>
				{/* Show errors in top msg bar */}
				{errors ? (
					<>
						<div style={{ paddingTop: '20px' }}></div>
						<div style={{ position: 'fixed' }}>
							<MessageBar intent='warning'>Oops! {errors} &nbsp;&nbsp;</MessageBar>
						</div>
					</>
				) : (
					<></>
				)}

				<div style={style.input}>
					<Label htmlFor={tabNameId}>Tab Name</Label>
					<Input
						ref={tabNameInput}
						aria-labelledby={tabNameId}
						id={tabNameId}
						defaultValue='Project Details'
					/>

					<Caption1 block={true}>
						Tab name, e.g. "Project Details" or "Schedule". You can change it later.
					</Caption1>
				</div>

				<div style={style.input}>
					<Label htmlFor={envComboId}>Environment</Label>
					<Dropdown id={envComboId} onOptionSelect={onEnvOptionSelect}>
						{environmentOptions.map((env) => (
							<Option text={env.FriendlyName} value={env.EnvironmentId}>
								{env.FriendlyName}
							</Option>
						))}
					</Dropdown>

					<Caption1 block={true}>Select the environment to show items from.</Caption1>
				</div>

				<div style={style.input}>
					<Label htmlFor={projComboId}>Project</Label>
					<Dropdown
						id={projComboId}
						onOptionSelect={onProjOptionSelect}
						disabled={!projectsLoaded}
						placeholder={projectPlaceholder}
					>
						{projectOptions.map((p) => (
							<Option text={p.name} value={p.id}>
								{p.name}
							</Option>
						))}
					</Dropdown>

					<Caption1 block={true}>Select the project form to display.</Caption1>
				</div>

				<div style={style.input}>
					<Label htmlFor={urlId}>URL *</Label>
					<Input
						ref={urlInput}
						aria-labelledby={`${urlId} ${beforeUrlId}`}
						placeholder='https://'
						value={urlString}
						id={urlId}
						onChange={onUrlChange}
						type='url'
					/>

					<Caption1 block={true}>
						Only URLs for <strong>*.*.dynamics.com</strong> domains are allowed.
					</Caption1>
				</div>

				<div style={{ marginTop: 'auto' }}>
					<Button onClick={handleSubmit} appearance='primary' size='medium'>
						Validate
					</Button>
				</div>
			</div>
		</div>
	);
};

export default TabConfig;
